import { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { Badge, Box, Link, List, ListItemButton, ListItemText } from '@mui/material';

import { useNotifications } from '../../contexts/NotificationsContext';

import styles from './styles';

const NavbarItemWithBadge = ({ path, location, text, Icon, isCollapsed, subItems }) => {
  const { notifications } = useNotifications();
  const currentLocation = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);

  const newNotificationsCount = notifications.filter(notification => !notification.read).length;
  const isActive = currentLocation.pathname.startsWith(path);
  const hasSubItems = subItems && subItems.length > 0;

  const handleClick = e => {
    if (hasSubItems) {
      e.preventDefault();
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <>
      <Link
        component={hasSubItems ? 'div' : RouterLink}
        to={hasSubItems ? undefined : `${path}${location}`}
        underline="none"
        color="inherit"
        style={{ cursor: hasSubItems ? 'pointer' : 'inherit' }}
      >
        <ListItemButton
          sx={{
            ...(isCollapsed ? styles.listItemButtonCollapsed : styles.listItemButton),
            ...(isActive && {
              bgcolor: 'var(--surface-overlay)',
              color: 'var(--text-primary)',
              borderLeftColor: 'var(--brand-coral)',
              '&:hover': {
                bgcolor: 'var(--surface-raised)',
                color: 'var(--text-primary)',
              },
            }),
          }}
          onClick={handleClick}
        >
          <Badge
            sx={isCollapsed ? styles.badgeCollapsed : styles.badge}
            badgeContent={newNotificationsCount}
            color="error"
          >
            <Icon sx={isCollapsed ? styles.navbarIconCollapsed : styles.navbarIcon} />
          </Badge>
          {!isCollapsed && (
            <>
              <ListItemText
                primary={text}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: isActive ? 600 : 500,
                }}
              />
              {hasSubItems && (
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    bgcolor: isExpanded ? 'var(--brand-coral)' : 'var(--text-placeholder)',
                    transition: 'all 0.2s ease',
                    ml: 'auto',
                  }}
                />
              )}
            </>
          )}
        </ListItemButton>
      </Link>
      {hasSubItems && isExpanded && !isCollapsed && (
        <List component="div" disablePadding>
          {subItems.map((subItem, subIndex) => {
            const isSubActive = currentLocation.pathname.startsWith(subItem.path);
            return (
              <Link
                key={subIndex}
                component={RouterLink}
                to={`${subItem.path}${location}`}
                underline="none"
                color="inherit"
              >
                <ListItemButton
                  sx={{
                    pl: 5,
                    py: 0.75,
                    mx: 0,
                    my: 0,
                    borderRadius: 0,
                    borderLeft: '2px solid transparent',
                    color: 'var(--text-secondary)',
                    fontSize: '0.8rem',
                    fontFamily: 'var(--font-data)',
                    fontWeight: 400,
                    transition: 'all 0.15s var(--easing)',
                    '&:hover': {
                      bgcolor: 'var(--surface-overlay)',
                      color: 'var(--text-primary)',
                      borderLeftColor: 'var(--border-default)',
                    },
                    ...(isSubActive && {
                      bgcolor: 'var(--surface-overlay)',
                      color: 'var(--text-primary)',
                      fontWeight: 500,
                      borderLeftColor: 'var(--brand-coral)',
                    }),
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      marginRight: '12px',
                      minWidth: '20px',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 3,
                        height: 3,
                        bgcolor: 'currentColor',
                        borderRadius: '50%',
                        opacity: 0.6,
                      }}
                    />
                  </Box>
                  <ListItemText
                    primary={subItem.text}
                    primaryTypographyProps={{
                      fontSize: '0.8rem',
                    }}
                  />
                </ListItemButton>
              </Link>
            );
          })}
        </List>
      )}
    </>
  );
};

export default NavbarItemWithBadge;
